import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";
import { MemberDevicesPanel } from "@/components/member-devices-panel";

type Member = {
  userId: string;
  email: string;
  name: string | null;
  role: string;
  /** H4: suspended from Admin → Access report. */
  suspendedAt?: string | null;
};

type Invite = {
  id: string;
  token: string;
  role: string;
  inviteUrl?: string;
  maxUses: number | null;
  useCount: number;
  expiresAt?: string | null;
};

type CourseOption = { id: string; title: string };

/**
 * PAY-7: student invites can name a course (including a private one) and waive its payment.
 * POST /v1/tenant/invites accepts `courseId` + `waivesPayment` and redeeming enrols the student.
 */
export const INVITE_COURSE_OPTIONS_ENABLED = true;

const STAFF_DEFAULT_USES = "1";
const STAFF_DEFAULT_DAYS = "7";
const STUDENT_DEFAULT_USES = "10";

type TenantMembersAdminProps = {
  tenantSlug: string;
  initialMembers: Member[];
  initialInvites: Invite[];
  currentUserId?: string;
  courseOptions?: boolean;
};

const inputClass =
  "rounded-md border border-[var(--border-strong)] bg-[var(--surface-1)] px-2 py-2 text-sm";
const secondaryButton =
  "rounded-md border border-[var(--border-strong)] px-3 py-1.5 text-sm hover:bg-[var(--surface-3)]";

function isExpired(invite: Invite): boolean {
  return Boolean(invite.expiresAt && new Date(invite.expiresAt).getTime() < Date.now());
}

function isUsedUp(invite: Invite): boolean {
  return invite.maxUses != null && invite.useCount >= invite.maxUses;
}

function positiveInt(value: string): number | undefined {
  const n = Number(value);
  return value.trim() && Number.isInteger(n) && n > 0 ? n : undefined;
}

export function TenantMembersAdmin({
  tenantSlug,
  initialMembers,
  initialInvites,
  currentUserId,
  courseOptions = INVITE_COURSE_OPTIONS_ENABLED,
}: TenantMembersAdminProps) {
  const t = useTranslations("admin");
  const tRoot = useTranslations();
  const locale = useLocale();
  const [members, setMembers] = useState(initialMembers);
  const [invites, setInvites] = useState(initialInvites);
  const [inviteRole, setInviteRole] = useState("student");
  const [maxUses, setMaxUses] = useState(STUDENT_DEFAULT_USES);
  const [expiresInDays, setExpiresInDays] = useState("");
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [courseId, setCourseId] = useState("");
  const [waivesPayment, setWaivesPayment] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!courseOptions) return;
    void (async () => {
      try {
        const res = await apiFetch("/v1/instructor/courses");
        if (!res.ok) return;
        const json = await res.json();
        setCourses((json.data.courses as CourseOption[]) ?? []);
      } catch {
        /* the selector just stays empty */
      }
    })();
  }, [courseOptions]);

  const showError = (err: unknown, fallbackKey: string) =>
    setError(apiErrorText(err, tRoot, fallbackKey, { namespace: "admin.errors" }));

  const roleLabel = (role: string) =>
    role === "instructor"
      ? t("roleInstructor")
      : role === "ta"
        ? t("roleTa")
        : role === "tenant_admin"
          ? t("roleTenantAdmin")
          : t("roleStudent");

  async function reloadData() {
    try {
      const [membersRes, invitesRes] = await Promise.all([
        apiFetch("/v1/tenant/members"),
        apiFetch("/v1/tenant/invites"),
      ]);
      if (!membersRes.ok) throw await readApiErrorResponse(membersRes);
      if (!invitesRes.ok) throw await readApiErrorResponse(invitesRes);
      const membersJson = await membersRes.json();
      const invitesJson = await invitesRes.json();
      setMembers(membersJson.data.members as Member[]);
      setInvites(invitesJson.data.invites as Invite[]);
    } catch (err) {
      showError(err, "admin.loadFailed");
    }
  }

  /** Runs a mutation; on success reloads the lists, on failure shows a translated reason. */
  async function mutate(path: string, init: RequestInit, fallbackKey: string) {
    setError(null);
    try {
      const res = await apiFetch(path, init);
      if (!res.ok) {
        const err = await readApiErrorResponse(res);
        showError(err, fallbackKey);
        if (err.code === "MEMBER_NOT_FOUND" || err.code === "INVITE_NOT_FOUND") {
          await reloadData();
        }
        return;
      }
      await reloadData();
    } catch (err) {
      showError(err, fallbackKey);
    }
  }

  function handleRoleChange(userId: string, role: string) {
    void mutate(
      `/v1/tenant/members/${userId}`,
      { method: "PATCH", body: JSON.stringify({ role }) },
      "admin.roleFailed",
    );
  }

  function handleRemove(member: Member) {
    if (!window.confirm(t("confirmRemove", { name: member.name ?? member.email }))) return;
    void mutate(`/v1/tenant/members/${member.userId}`, { method: "DELETE" }, "admin.removeFailed");
  }

  function handleRevoke(invite: Invite) {
    if (!window.confirm(t("confirmRevoke"))) return;
    void mutate(`/v1/tenant/invites/${invite.id}`, { method: "DELETE" }, "admin.revokeFailed");
  }

  function handleInviteRoleChange(role: string) {
    setInviteRole(role);
    // SEC-8: a staff link is a credential — suggest single use and a short life.
    if (role === "student") {
      setMaxUses(STUDENT_DEFAULT_USES);
      setExpiresInDays("");
    } else {
      setMaxUses(STAFF_DEFAULT_USES);
      setExpiresInDays(STAFF_DEFAULT_DAYS);
      setCourseId("");
      setWaivesPayment(false);
    }
  }

  function handleCreateInvite(e: React.FormEvent) {
    e.preventDefault();
    const body: Record<string, unknown> = { role: inviteRole };
    const uses = positiveInt(maxUses);
    const days = positiveInt(expiresInDays);
    if (uses !== undefined) body.maxUses = uses;
    if (days !== undefined) body.expiresInDays = days;
    if (courseOptions && courseId) {
      body.courseId = courseId;
      body.waivesPayment = waivesPayment;
    }
    void mutate(
      "/v1/tenant/invites",
      { method: "POST", body: JSON.stringify(body) },
      "admin.inviteFailed",
    );
  }

  async function handleCopy(invite: Invite) {
    const url = `${window.location.origin}${invite.inviteUrl ?? ""}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(invite.id);
    } catch {
      setError(t("copyFailed"));
    }
  }

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(new Date(iso));

  return (
    <div className="space-y-10">
      <p className="text-xs text-[var(--text-muted)]">{t("tenantLabel", { tenant: tenantSlug })}</p>
      {error && (
        <p className="text-sm text-red-600" role="alert" data-testid="admin-members-error">
          {error}
        </p>
      )}

      <section>
        <h2 className="text-lg font-medium">{t("membersHeading")}</h2>
        <ul className="mt-4 divide-y divide-[var(--border)] rounded-lg border border-[var(--border)]">
          {members.map((member) => {
            const name = member.name ?? member.email;
            const isSelf = member.userId === currentUserId;
            return (
              <li
                key={member.userId}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                data-testid={`member-row-${member.userId}`}
              >
                <div>
                  <p className="font-medium">
                    {name}{" "}
                    {isSelf ? <span className="text-[var(--text-muted)]">{t("you")}</span> : null}
                  </p>
                  <p className="text-sm text-[var(--text-muted)]">{member.email}</p>
                  {member.suspendedAt ? (
                    <p
                      className="mt-1 inline-block rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800"
                      data-testid={`member-suspended-${member.userId}`}
                    >
                      {t("suspendedBadge")}
                    </p>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  {member.role === "tenant_admin" ? (
                    <span className="text-sm text-[var(--text-muted)]">
                      {roleLabel(member.role)}
                    </span>
                  ) : (
                    <select
                      value={member.role}
                      onChange={(e) => handleRoleChange(member.userId, e.target.value)}
                      className={inputClass + " py-1"}
                      aria-label={t("roleSelectLabel", { name })}
                      data-testid={`member-role-${member.userId}`}
                    >
                      <option value="instructor">{t("roleInstructor")}</option>
                      <option value="ta">{t("roleTa")}</option>
                      <option value="student">{t("roleStudent")}</option>
                    </select>
                  )}
                  {isSelf ? null : (
                    <button
                      type="button"
                      onClick={() => handleRemove(member)}
                      className={secondaryButton + " text-red-700"}
                      aria-label={t("removeMemberLabel", { name })}
                      data-testid={`member-remove-${member.userId}`}
                    >
                      {t("removeMember")}
                    </button>
                  )}
                </div>
                {/* H3: where a student is signed in, and "Sign out all devices". */}
                {member.role === "student" ? (
                  <MemberDevicesPanel userId={member.userId} name={name} />
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>

      <section data-testid="add-member-guidance">
        <h2 className="text-lg font-medium">{t("addGuidanceTitle")}</h2>
        <p className="mt-2 text-sm text-[var(--text-muted)]">{t("addGuidance")}</p>
      </section>

      <section>
        <h2 className="text-lg font-medium">{t("inviteLinks")}</h2>
        <form onSubmit={handleCreateInvite} className="mt-4 flex flex-wrap items-end gap-3">
          <label className="flex flex-col text-sm">
            {t("inviteRoleLabel")}
            <select
              value={inviteRole}
              onChange={(e) => handleInviteRoleChange(e.target.value)}
              className={inputClass + " mt-1"}
              data-testid="create-invite-role"
            >
              <option value="instructor">{t("roleInstructor")}</option>
              <option value="ta">{t("roleTa")}</option>
              <option value="student">{t("roleStudent")}</option>
            </select>
          </label>
          <label className="flex flex-col text-sm">
            {t("inviteMaxUsesLabel")}
            <input
              type="number"
              min={1}
              max={10000}
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              className={inputClass + " mt-1 w-28"}
              data-testid="create-invite-max-uses"
            />
          </label>
          <label className="flex flex-col text-sm">
            {t("inviteExpiresLabel")}
            <input
              type="number"
              min={1}
              max={365}
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value)}
              className={inputClass + " mt-1 w-28"}
              data-testid="create-invite-expires"
            />
          </label>
          {courseOptions && inviteRole === "student" ? (
            <>
              <label className="flex flex-col text-sm">
                {t("inviteCourseLabel")}
                <select
                  value={courseId}
                  onChange={(e) => {
                    setCourseId(e.target.value);
                    if (!e.target.value) setWaivesPayment(false);
                  }}
                  className={inputClass + " mt-1"}
                  data-testid="create-invite-course"
                >
                  <option value="">{t("inviteNoCourse")}</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={waivesPayment}
                  disabled={!courseId}
                  onChange={(e) => setWaivesPayment(e.target.checked)}
                  data-testid="create-invite-waive"
                />
                {t("inviteWaiveLabel")}
              </label>
            </>
          ) : null}
          <button
            type="submit"
            className="rounded-md bg-[var(--accent-strong)] px-4 py-2 text-sm text-white hover:bg-[var(--accent-strong-hover)]"
            data-testid="create-invite-submit"
          >
            {t("createInvite")}
          </button>
        </form>
        <p className="mt-2 text-xs text-[var(--text-muted)]">{t("inviteLimitsHint")}</p>

        {invites.length === 0 ? (
          <p className="mt-4 text-sm text-[var(--text-muted)]">{t("noInvites")}</p>
        ) : (
          <ul className="mt-4 space-y-2">
            {invites.map((invite) => {
              const expired = isExpired(invite);
              const usedUp = isUsedUp(invite);
              return (
                <li
                  key={invite.id}
                  className="space-y-2 rounded-md border border-[var(--border)] px-3 py-2 text-sm"
                  data-testid={`invite-row-${invite.id}`}
                >
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-medium">{roleLabel(invite.role)}</span>
                    <span className="text-[var(--text-muted)]">·</span>
                    <span>
                      {invite.maxUses == null
                        ? t("inviteUsesUnlimited", { used: invite.useCount })
                        : t("inviteUses", { used: invite.useCount, max: invite.maxUses })}
                    </span>
                    <span className="text-[var(--text-muted)]">·</span>
                    <span>
                      {expired
                        ? t("inviteExpired")
                        : invite.expiresAt
                          ? t("inviteExpiresOn", { date: formatDate(invite.expiresAt) })
                          : t("inviteNoExpiry")}
                    </span>
                    {usedUp ? (
                      <span className="rounded bg-[var(--surface-3)] px-1.5 text-xs">
                        {t("inviteUsedUp")}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="break-all text-xs">
                      {invite.inviteUrl ? `${window.location.origin}${invite.inviteUrl}` : ""}
                    </code>
                    <button
                      type="button"
                      onClick={() => void handleCopy(invite)}
                      className={secondaryButton}
                      data-testid={`invite-copy-${invite.id}`}
                    >
                      {copiedId === invite.id ? t("copied") : t("copyLink")}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRevoke(invite)}
                      className={secondaryButton + " text-red-700"}
                      aria-label={t("revokeInviteLabel", { role: roleLabel(invite.role) })}
                      data-testid={`invite-revoke-${invite.id}`}
                    >
                      {t("revokeInvite")}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
